/**
 * Bill of Lading Transport Fields Component
 *
 * Renders the transport plan section of the B/L form.
 * Each transport leg contains: stageSequence, stageType, modeOfTransport,
 * vessel info, carrier service info, load/discharge locations and dates.
 *
 * Stage type codes:
 * - PRC = Pre-carriage
 * - MNC = Main Carriage
 * - ONC = On-carriage
 *
 * Mode of transport codes:
 * - VESSEL, TRUCK, RAIL, BARGE
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

const STAGE_TYPES = [
  { value: 'PRC', label: 'PRC - Pre-carriage' },
  { value: 'MNC', label: 'MNC - Main Carriage' },
  { value: 'ONC', label: 'ONC - On-carriage' }
];

const STAGE_TYPE_LABELS = {
  PRC: 'Pre-carriage',
  MNC: 'Main Carriage',
  ONC: 'On-carriage'
};

const MODE_OF_TRANSPORT_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'VESSEL', label: 'Vessel' },
  { value: 'TRUCK', label: 'Truck' },
  { value: 'RAIL', label: 'Rail' },
  { value: 'BARGE', label: 'Barge' }
];

/**
 * BLTransportFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLTransportFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transportPlan'
  });

  const addLeg = () => {
    append({
      stageSequence: fields.length + 1,
      stageType: 'MNC',
      modeOfTransport: 'VESSEL',
      vesselName: '',
      vesselIMO: '',
      voyageNumber: '',
      carrierServiceCode: '',
      carrierServiceName: '',
      loadLocation: '',
      loadLocationUNCode: '',
      dischargeLocation: '',
      dischargeLocationUNCode: '',
      plannedDeparture: '',
      plannedArrival: ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">Transport Plan</h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addLeg}>
            + Add Stage
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No transport stages added yet. Click "Add Stage" to specify routing.
        </div>
      )}

      {/* Transport leg entries */}
      {fields.map((field, index) => (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
          {/* Stage header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {STAGE_TYPE_LABELS[field.stageType] || `Stage #${index + 1}`}
            </span>
            {isEditMode && (
              <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700 text-sm">
                Remove
              </button>
            )}
          </div>

          {/* Stage core fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <Input
              label="Stage Sequence"
              type="number"
              {...register(`transportPlan.${index}.stageSequence`, { valueAsNumber: true })}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].stageSequence`)}
            />

            {/* Stage Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stage Type</label>
              <select
                {...register(`transportPlan.${index}.stageType`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {STAGE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Mode of Transport */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode of Transport</label>
              <select
                {...register(`transportPlan.${index}.modeOfTransport`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {MODE_OF_TRANSPORT_OPTIONS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Vessel information */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Vessel / Carrier</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input
                label="Vessel Name"
                {...register(`transportPlan.${index}.vesselName`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].vesselName`)}
              />

              <Input
                label="Vessel IMO"
                {...register(`transportPlan.${index}.vesselIMO`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].vesselIMO`)}
                placeholder="e.g., 9321483"
              />

              <Input
                label="Voyage Number"
                {...register(`transportPlan.${index}.voyageNumber`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].voyageNumber`)}
              />

              <Input
                label="Carrier Service Code"
                {...register(`transportPlan.${index}.carrierServiceCode`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].carrierServiceCode`)}
              />

              <Input
                label="Carrier Service Name"
                {...register(`transportPlan.${index}.carrierServiceName`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].carrierServiceName`)}
              />
            </div>
          </div>

          {/* Load and discharge locations */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Locations</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input
                label="Load Location"
                {...register(`transportPlan.${index}.loadLocation`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].loadLocation`)}
                placeholder="Port or place name"
              />

              <Input
                label="Load Location UN Code"
                {...register(`transportPlan.${index}.loadLocationUNCode`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].loadLocationUNCode`)}
                placeholder="e.g., CNSHA"
              />

              <Input
                label="Discharge Location"
                {...register(`transportPlan.${index}.dischargeLocation`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].dischargeLocation`)}
                placeholder="Port or place name"
              />

              <Input
                label="Discharge Location UN Code"
                {...register(`transportPlan.${index}.dischargeLocationUNCode`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].dischargeLocationUNCode`)}
                placeholder="e.g., USLAX"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Schedule</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Planned Departure"
                type="date"
                {...register(`transportPlan.${index}.plannedDeparture`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].plannedDeparture`)}
              />

              <Input
                label="Planned Arrival"
                type="date"
                {...register(`transportPlan.${index}.plannedArrival`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `transportPlan[${index}].plannedArrival`)}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default BLTransportFields;
